import type {
  JsonValue,
  PrefillDto,
  ResolvedContractorDocumentRequirementDto,
  ResolvedContractorFieldDto,
  ResolvedContractorFormSchemaDto,
  TemplateDocumentMetadataFieldDefinitionDto,
  UpdateContractorPrefillRequest,
} from './api';

export interface ConfigurationValuesView {
  readonly fields: Record<string, JsonValue>;
  readonly documents: Record<
    string,
    {
      readonly document_type_id: string;
      readonly subject_binding: string;
      readonly metadata: Record<string, JsonValue>;
    }
  >;
}

export interface FieldEditState {
  readonly [fieldKey: string]: JsonValue | undefined;
}

export interface DocumentEditState {
  readonly [documentIdentity: string]: {
    readonly present: boolean;
    readonly metadata: Record<string, JsonValue>;
  };
}

/** Canonical persistence key matching backend fieldStorageKey (internal only). */
export function fieldStorageKey(field: ResolvedContractorFieldDto): string {
  if (field.binding_kind === 'CASE_SNAPSHOT') {
    return field.snapshot_path ?? field.field_key;
  }
  if (field.binding_kind === 'CASE_DYNAMIC') {
    return field.semantic_key ?? field.field_key;
  }
  if (field.checklist_template_id !== null && field.field_definition_id !== null) {
    return `${field.checklist_template_id}:${field.field_definition_id}`;
  }
  return field.field_key;
}

export function documentIdentityKey(documentTypeId: string, subjectBinding: string): string {
  return `${documentTypeId}::${subjectBinding}`;
}

export function readConfigurationValues(data: Record<string, JsonValue>): ConfigurationValuesView {
  const raw = data.configuration_values;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { fields: {}, documents: {} };
  }
  const root = raw as Record<string, JsonValue>;
  const fieldsRaw = root.fields;
  const documentsRaw = root.documents;
  const fields: Record<string, JsonValue> = {};
  if (typeof fieldsRaw === 'object' && fieldsRaw !== null && !Array.isArray(fieldsRaw)) {
    for (const [key, value] of Object.entries(fieldsRaw)) {
      fields[key] = value;
    }
  }
  const documents: ConfigurationValuesView['documents'] = {};
  if (typeof documentsRaw === 'object' && documentsRaw !== null && !Array.isArray(documentsRaw)) {
    for (const [key, value] of Object.entries(documentsRaw)) {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        continue;
      }
      const row = value as Record<string, JsonValue>;
      if (typeof row.document_type_id !== 'string' || typeof row.subject_binding !== 'string') {
        continue;
      }
      const metadataRaw = row.metadata;
      const metadata: Record<string, JsonValue> = {};
      if (typeof metadataRaw === 'object' && metadataRaw !== null && !Array.isArray(metadataRaw)) {
        for (const [metaKey, metaValue] of Object.entries(metadataRaw)) {
          metadata[metaKey] = metaValue;
        }
      }
      documents[key] = {
        document_type_id: row.document_type_id,
        subject_binding: row.subject_binding,
        metadata,
      };
    }
  }
  return { fields, documents };
}

function readPath(data: Record<string, JsonValue>, path: string): JsonValue | undefined {
  const parts = path.split('.');
  let current: JsonValue | undefined = data;
  for (const part of parts) {
    if (typeof current !== 'object' || current === null || Array.isArray(current)) {
      return undefined;
    }
    current = (current as Record<string, JsonValue>)[part];
  }
  return current;
}

export function readPersistedFieldValue(
  field: ResolvedContractorFieldDto,
  data: Record<string, JsonValue>,
): JsonValue | undefined {
  const stored = readConfigurationValues(data).fields[fieldStorageKey(field)];
  if (stored !== undefined) {
    return stored;
  }
  if (field.binding_kind === 'CASE_SNAPSHOT' && field.snapshot_path) {
    return readPath(data, field.snapshot_path);
  }
  return undefined;
}

export function isMetadataFieldRequired(
  field: TemplateDocumentMetadataFieldDefinitionDto,
): boolean {
  const rule = field.required_rule;
  return rule.type === 'ALWAYS';
}

export function readEnumOptions(validation: Record<string, JsonValue>): readonly string[] {
  const raw = validation.enum ?? validation.allowed_values;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((row): row is string => typeof row === 'string');
}

export function groupFieldsByPlacement(fields: readonly ResolvedContractorFieldDto[]): readonly {
  readonly placement: string;
  readonly fields: readonly ResolvedContractorFieldDto[];
}[] {
  const order: string[] = [];
  const buckets = new Map<string, ResolvedContractorFieldDto[]>();
  for (const field of fields) {
    const key = field.placement || 'OTHER';
    if (!buckets.has(key)) {
      order.push(key);
      buckets.set(key, []);
    }
    buckets.get(key)!.push(field);
  }
  return order.map((placement) => ({
    placement,
    fields: buckets.get(placement) ?? [],
  }));
}

/** Prefer server field_groups order when present; otherwise placement buckets. */
export function resolveFieldGroups(schema: ResolvedContractorFormSchemaDto): readonly {
  readonly id: string;
  readonly title: string;
  readonly fields: readonly ResolvedContractorFieldDto[];
}[] {
  const groupsRaw = schema.field_groups;
  const orderedKeys = Object.keys(groupsRaw);
  if (orderedKeys.length > 0) {
    const byKey = new Map(schema.fields.map((field) => [field.field_key, field]));
    const used = new Set<string>();
    const groups: { id: string; title: string; fields: ResolvedContractorFieldDto[] }[] = [];
    for (const groupId of orderedKeys) {
      const entry = groupsRaw[groupId];
      let title = groupId;
      let fieldKeys: string[] = [];
      if (typeof entry === 'object' && entry !== null && !Array.isArray(entry)) {
        const obj = entry as Record<string, JsonValue>;
        if (typeof obj.title === 'string') {
          title = obj.title;
        }
        if (Array.isArray(obj.field_keys)) {
          fieldKeys = obj.field_keys.filter((row): row is string => typeof row === 'string');
        } else if (Array.isArray(obj.fields)) {
          fieldKeys = obj.fields.filter((row): row is string => typeof row === 'string');
        }
      }
      const fields = fieldKeys
        .map((key) => byKey.get(key))
        .filter((field): field is ResolvedContractorFieldDto => field !== undefined);
      for (const field of fields) {
        used.add(field.field_key);
      }
      if (fields.length > 0) {
        groups.push({ id: groupId, title, fields });
      }
    }
    const remainder = schema.fields.filter((field) => !used.has(field.field_key));
    if (remainder.length > 0) {
      groups.push({ id: '_remaining', title: 'Дополнительно', fields: remainder });
    }
    return groups;
  }
  return groupFieldsByPlacement(schema.fields).map((group) => ({
    id: group.placement,
    title: placementTitle(group.placement),
    fields: group.fields,
  }));
}

function placementTitle(placement: string): string {
  switch (placement) {
    case 'HEADER':
      return 'Основные сведения';
    case 'OBJECT_BLOCK':
      return 'Объект осмотра';
    case 'RESULT_BLOCK':
      return 'Результаты';
    case 'SIGNATURE_BLOCK':
      return 'Подписи';
    default:
      return placement;
  }
}

export function initializeFieldEdits(
  schema: ResolvedContractorFormSchemaDto | null | undefined,
  data: Record<string, JsonValue>,
): FieldEditState {
  if (schema === null || schema === undefined) {
    return {};
  }
  const edits: Record<string, JsonValue | undefined> = {};
  for (const field of schema.fields) {
    edits[field.field_key] = readPersistedFieldValue(field, data);
  }
  return edits;
}

export function initializeDocumentEdits(
  requirements: readonly ResolvedContractorDocumentRequirementDto[],
  data: Record<string, JsonValue>,
): DocumentEditState {
  const stored = readConfigurationValues(data).documents;
  const edits: Record<string, { present: boolean; metadata: Record<string, JsonValue> }> = {};
  for (const doc of requirements) {
    const key = documentIdentityKey(doc.document_type_id, doc.subject_binding);
    const existing = stored[key];
    edits[key] = {
      present: existing !== undefined,
      metadata: existing?.metadata ?? {},
    };
  }
  return edits;
}

export function documentRequirementsFromPrefill(
  prefill: PrefillDto,
): readonly ResolvedContractorDocumentRequirementDto[] {
  if (prefill.resolved_form_schema?.document_requirements !== undefined) {
    return prefill.resolved_form_schema.document_requirements;
  }
  return prefill.document_requirements ?? [];
}

export function buildUpdatePrefillRequest(input: {
  readonly prefill: PrefillDto;
  readonly fieldEdits: FieldEditState;
  readonly documentEdits: DocumentEditState;
  readonly inspectionSiteId: string;
  readonly equipmentTypeId: string;
}): UpdateContractorPrefillRequest {
  const schema = input.prefill.resolved_form_schema;
  const configuration = input.prefill.configuration;
  if (
    schema === null ||
    schema === undefined ||
    configuration === null ||
    configuration === undefined
  ) {
    throw new Error('Configuration must be bound before update');
  }
  const field_values = schema.fields
    .filter((field) => field.owner_code === 'CONTRACTOR' && field.applicable)
    .map((field) => ({
      field_key: field.field_key,
      value: normalizeOutgoingValue(field.value_type, input.fieldEdits[field.field_key]),
    }))
    .filter((row) => row.value !== undefined)
    .map((row) => ({ field_key: row.field_key, value: row.value as JsonValue }));

  const document_values = documentRequirementsFromPrefill(input.prefill)
    .filter((doc) => doc.applicable)
    .map((doc) => {
      const key = documentIdentityKey(doc.document_type_id, doc.subject_binding);
      const edit = input.documentEdits[key];
      if (edit === undefined || !edit.present) {
        return null;
      }
      return {
        document_type_id: doc.document_type_id,
        subject_binding: doc.subject_binding,
        metadata: { ...edit.metadata },
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  return {
    expected_case_version: input.prefill.case_version,
    expected_prefill_version: input.prefill.prefill_version ?? '0',
    configuration_plan_revision_id: configuration.plan_revision_id,
    configuration_fingerprint: configuration.configuration_fingerprint,
    inspection_site_id: input.inspectionSiteId,
    equipment_type_id: input.equipmentTypeId,
    field_values,
    document_values,
  };
}

export function revalidationFormSchema(input: {
  readonly fingerprint: string;
  readonly fields: readonly ResolvedContractorFieldDto[];
  readonly documents: readonly ResolvedContractorDocumentRequirementDto[];
}): ResolvedContractorFormSchemaDto {
  return {
    configuration_fingerprint: input.fingerprint,
    field_groups: {},
    fields: input.fields,
    document_requirements: input.documents,
    validation_summary: {},
  };
}

export function buildUpdateRevalidationRequest(input: {
  readonly caseVersion: string;
  readonly revalidationVersion: string;
  readonly configurationFingerprint: string;
  readonly fields: readonly ResolvedContractorFieldDto[];
  readonly documents: readonly ResolvedContractorDocumentRequirementDto[];
  readonly fieldEdits: FieldEditState;
  readonly documentEdits: DocumentEditState;
}): {
  readonly expected_case_version: string;
  readonly expected_revalidation_version: string;
  readonly configuration_fingerprint: string;
  readonly field_values: readonly { readonly field_key: string; readonly value: JsonValue }[];
  readonly document_values: readonly {
    readonly document_type_id: string;
    readonly subject_binding: string;
    readonly metadata: Record<string, JsonValue>;
  }[];
} {
  const field_values = input.fields
    .filter((field) => field.owner_code === 'CONTRACTOR' && field.applicable)
    .map((field) => ({
      field_key: field.field_key,
      value: normalizeOutgoingValue(field.value_type, input.fieldEdits[field.field_key]),
    }))
    .filter((row) => row.value !== undefined)
    .map((row) => ({ field_key: row.field_key, value: row.value as JsonValue }));

  const document_values = input.documents
    .filter((doc) => doc.applicable)
    .map((doc) => {
      const key = documentIdentityKey(doc.document_type_id, doc.subject_binding);
      const edit = input.documentEdits[key];
      if (edit === undefined || !edit.present) {
        return null;
      }
      return {
        document_type_id: doc.document_type_id,
        subject_binding: doc.subject_binding,
        metadata: { ...edit.metadata },
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  return {
    expected_case_version: input.caseVersion,
    expected_revalidation_version: input.revalidationVersion,
    configuration_fingerprint: input.configurationFingerprint,
    field_values,
    document_values,
  };
}

export function initializeEmptyFieldEdits(
  fields: readonly ResolvedContractorFieldDto[],
): FieldEditState {
  const edits: Record<string, JsonValue | undefined> = {};
  for (const field of fields) {
    edits[field.field_key] = undefined;
  }
  return edits;
}

export function initializeEmptyDocumentEdits(
  requirements: readonly ResolvedContractorDocumentRequirementDto[],
): DocumentEditState {
  const edits: Record<string, { present: boolean; metadata: Record<string, JsonValue> }> = {};
  for (const doc of requirements) {
    const key = documentIdentityKey(doc.document_type_id, doc.subject_binding);
    edits[key] = { present: false, metadata: {} };
  }
  return edits;
}

function normalizeOutgoingValue(
  valueType: string,
  value: JsonValue | undefined,
): JsonValue | undefined {
  if (value === undefined || value === null) {
    return null;
  }
  if (valueType === 'NUMBER') {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : value;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }
  return value;
}

export function clientVisibleRequiredIssues(input: {
  readonly schema: ResolvedContractorFormSchemaDto | null | undefined;
  readonly documents: readonly ResolvedContractorDocumentRequirementDto[];
  readonly fieldEdits: FieldEditState;
  readonly documentEdits: DocumentEditState;
}): readonly string[] {
  const summary = summarizeClientVisibleRequiredIssues(input);
  return [...summary.fieldLabels, ...summary.documentLabels];
}

export function summarizeClientVisibleRequiredIssues(input: {
  readonly schema: ResolvedContractorFormSchemaDto | null | undefined;
  readonly documents: readonly ResolvedContractorDocumentRequirementDto[];
  readonly fieldEdits: FieldEditState;
  readonly documentEdits: DocumentEditState;
}): {
  readonly fieldCount: number;
  readonly documentCount: number;
  readonly fieldLabels: readonly string[];
  readonly documentLabels: readonly string[];
} {
  const fieldLabels: string[] = [];
  const documentLabels: string[] = [];
  if (input.schema !== null && input.schema !== undefined) {
    for (const field of input.schema.fields) {
      if (!field.applicable || !field.required || field.owner_code !== 'CONTRACTOR') {
        continue;
      }
      const value = input.fieldEdits[field.field_key];
      if (value === undefined || value === null || value === '') {
        fieldLabels.push(field.label);
      }
    }
  }
  for (const doc of input.documents) {
    if (!doc.applicable || !doc.required) {
      continue;
    }
    const key = documentIdentityKey(doc.document_type_id, doc.subject_binding);
    const edit = input.documentEdits[key];
    if (edit === undefined || !edit.present) {
      documentLabels.push(doc.display_label);
      continue;
    }
    let metaMissing = false;
    for (const meta of doc.metadata_fields) {
      if (!isMetadataFieldRequired(meta)) {
        continue;
      }
      const metaValue = edit.metadata[meta.field_code];
      if (metaValue === undefined || metaValue === null || metaValue === '') {
        metaMissing = true;
        break;
      }
    }
    if (metaMissing) {
      documentLabels.push(doc.display_label);
    }
  }
  return {
    fieldCount: fieldLabels.length,
    documentCount: documentLabels.length,
    fieldLabels,
    documentLabels,
  };
}

export function isDocumentEditComplete(
  doc: ResolvedContractorDocumentRequirementDto,
  edit: DocumentEditState[string] | undefined,
): boolean {
  if (edit === undefined || !edit.present) {
    return false;
  }
  if (doc.capture_mode === 'PRESENCE_ONLY') {
    return true;
  }
  for (const meta of doc.metadata_fields) {
    if (!isMetadataFieldRequired(meta)) {
      continue;
    }
    const metaValue = edit.metadata[meta.field_code];
    if (metaValue === undefined || metaValue === null || metaValue === '') {
      return false;
    }
  }
  return true;
}

export function isContractorEditable(field: ResolvedContractorFieldDto): boolean {
  return field.owner_code === 'CONTRACTOR' && field.applicable;
}
